import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';

import ENDPOINTS from 'api/utils/endpoints';
import { get } from 'api/utils/fetch';
import useQueryKeys from 'api/utils/useQueryKeys';
import { userAtom } from 'store';
import { assertIsDefined } from 'util/assert';

import { accessControlDataSchema } from './accessControlSchema';
import { useSelectedDealerId } from '@lotlinx-aws/dealer-selector';

export interface AccessControlData {
  isVmbiSubscribedOnly: boolean;
}

async function fetchAccessControl(token?: string, dealerId?: number) {
  if (!token) {
    return { isVmbiSubscribedOnly: false };
  }
  assertIsDefined(token);
  assertIsDefined(dealerId);
  const res = await get<AccessControlData>(`${ENDPOINTS.access}/${dealerId}`, {
    headers: { token },
  });

  // Validate response
  const zodValidation = accessControlDataSchema.safeParse(res);

  if (token && !zodValidation.success) {
    Sentry.captureException(zodValidation.error);
    // For boolean responses, we can't filter errors the same way, so we'll use a default value
    return { isVmbiSubscribedOnly: false };
  }

  return res;
}

export default function useAccessControl() {
  const [user] = useAtom(userAtom);
  const [dealerId] = useSelectedDealerId();
  const { accessControlKey: queryKey } = useQueryKeys();

  return useQuery<AccessControlData, Error>({
    queryKey,
    queryFn: () => fetchAccessControl(user?.token, dealerId),
    staleTime: 10 * 60 * 1000, // 10 minutes
    enabled: !!user?.id && !!dealerId, // Only run when user and dealer are loaded
  });
}
